import { useEffect } from "react";
import { FormularioAddEditFactorInterface } from "../interfaces/formularioAddEditFactor";
import { Dialog, DialogContent, DialogTitle, Grid2 } from "@mui/material";
import MButton from "../../../../components/inputs/yup/materialui/mButton";
import InputSelect from "../../../../components/inputs/yup/materialui/inputSelect";
import InputText from "../../../../components/inputs/yup/materialui/inputText";
import * as Yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from "react-hook-form";
import { optionsMonths } from "../utils/optionsMonths";
import { PropsModalEdit } from "../interfaces/PropsModalEdit.interface";
import { upsertMesAnterior } from "../services/correccionMonetariaService";
import { UpsertMesAnteriorInterface } from "../models/UpsertMesAnterior";
import { useToast } from "../../../../hooks/useToast";

const ModalAddEditFactor = ({show, onHide, anno, mesComercial, formularioDefecto, isUpdate, onComplete} : PropsModalEdit) => {
    
    const { addToast } = useToast();

    const schema = Yup.object<FormularioAddEditFactorInterface>().shape({
        annoComercial: Yup.number().required('El año comercial es requerido').max(new Date().getFullYear() - 1, `El año comercial debe ser como maximo ${ new Date().getFullYear() - 1}`),
        mesComercial: Yup.number().required('El mes comercial es requerido'),
        mesCalendario: Yup.number().required('El mes calendario es requerido'),
        factor: Yup.string()
            .required('El factor es requerido')
            .max(7, 'Máximo 7 caracteres (incluye el signo)')
            .test('solo-caracteres-validos', 'Solo números, "-", y coma (",")', v => {
                if (typeof v !== 'string') return false;
                return /^[\d\-,]*$/.test(v);  // solo dígitos, guion y coma
            })
            .test('estructura-valida', 'Formato inválido. Usa hasta 6 dígitos enteros y coma para decimales (ej: -123456,78)', v => {
                if (typeof v !== 'string') return false;

                // 1) detectar signo (en cualquier parte)
                const negative = v.includes('-');

                // 2) eliminar todo excepto dígitos y coma
                let cleaned = v.replace(/[^0-9,]/g, '');

                if (!cleaned) return false;

                // 3) si hay varias comas, quedarnos con la primera
                const firstComma = cleaned.indexOf(',');
                if (firstComma !== -1) {
                    cleaned =
                    cleaned.slice(0, firstComma + 1) +
                    cleaned.slice(firstComma + 1).replace(/,/g, '');
                }

                // 4) validar partes
                const [entera, decimal = ''] = cleaned.split(',');
                if (!entera || !/^\d+$/.test(entera)) return false;
                if (entera.length > 6) return false; // regla de 6 dígitos máx en parte entera
                if (decimal && !/^\d+$/.test(decimal)) return false;

                // Si llegó acá, está OK
                return true;
            }),
    });

    const {
        control,
        formState: { errors, isDirty },
        handleSubmit,
        setValue
    } = useForm<FormularioAddEditFactorInterface>({
        resolver: yupResolver(schema),
        mode: 'onChange',
    })

    const loadFormulario = async () => {

        setValue('annoComercial', anno);
        setValue('mesComercial', mesComercial);
        setValue('mesCalendario', 1);

        if (formularioDefecto) {
            setValue('mesCalendario', formularioDefecto.mesCalendario);
            const valor = String(formularioDefecto.factor ?? '').replace('.', ',');
            setValue('factor', valor);
        };

    }

    useEffect(() => {
        loadFormulario();
    }, [formularioDefecto, show]);

    const showToast = (title: string, tipo: "Success" | "Danger" | "Warning" | "Info" | "Dark" | "Light", mensaje: string) => {
        addToast({
          mostrar: true,
          titulo: title,
          tipo,
          mensaje,
        });
    }

    const permitirTeclaFactor = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const allowedNav = ['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End','Enter'];
        if (allowedNav.includes(e.key)) return;
      
        if (/^\d$/.test(e.key)) return;  // dígitos
        if (e.key === ',' || e.key === '-') return; // permitir coma y guion en cualquier parte
      
        e.preventDefault(); // bloquear todo lo demás (incluye '.')
      };
      

    const onSubmit = async (formulario: FormularioAddEditFactorInterface) => {
        if(!isDirty) return;

        const factorNum = parseFloat(
            (formulario.factor ?? '').toString().replace(',', '.')
        );

        const body: UpsertMesAnteriorInterface = {
            anno: formulario.annoComercial,
            mesComercial: formulario.mesComercial,
            mesCalendario: formulario.mesCalendario,
            factor: factorNum,
            editar: isUpdate ? true : false,
        }

        const response = await upsertMesAnterior(body);

        if( response.status === 200 ){
            showToast('Corrección monetaria', 'Success', `${isUpdate ? 'Factor actualizado correctamente.' : 'Factor agregado correctamente.'}`)
            onComplete();
            onHide();
        }

    }

    return(
        <Dialog
            open={show}
            onClose={onHide}
            maxWidth='xl'
        >
            <DialogTitle>
                {(formularioDefecto ? "Editar " : "Crear") + " factor"}
            </DialogTitle>
            <DialogContent>
                <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
                    <Grid2 size={{ xs: 12, lg: 6 }}>
                        <InputText
                            label="Año comercial"
                            name="annoComercial"
                            control={control}
                            readonly
                            desabilitado
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 6 }}>
                        <InputSelect
                            label="Mes comercial"
                            name="mesComercial"
                            control={control}
                            options={optionsMonths}
                            error={errors.mesComercial}
                            clearable={false}
                            readonly
                            disabled
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 6 }}>
                        <InputSelect
                            label="Mes calendario"
                            name="mesCalendario"
                            control={control}
                            options={optionsMonths}
                            error={errors.mesCalendario}
                            clearable={false}
                            disabled={isUpdate ? true : false}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 6 }}>
                        <InputText
                            label="Factor [9,9]"
                            name="factor"
                            control={control}
                            error={errors.factor}
                            inputProps={{
                                maxLength: 7,          // 6 dígitos + opcional '-'
                                inputMode: 'decimal',
                                placeholder: "Ej: -3,45",
                              }}
                            onKeyDown={permitirTeclaFactor}
                        />
                    </Grid2>
                </Grid2>
                <Grid2 size={4} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "15px" }}>
                    <MButton onClick={onHide} variant="contained" color="inherit">
                        Cancelar
                    </MButton>
                    <MButton onClick={handleSubmit(onSubmit)} variant="contained" color="primary" style={{marginLeft: "10px"}}>
                        {formularioDefecto ? 'Editar' : 'Agregar'}
                    </MButton>
                </Grid2>
            </DialogContent>
        </Dialog> 
    )

}

export default ModalAddEditFactor;